import { usePathname } from 'next/navigation';
import { useEffect, useCallback, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { PANEL_LAYOUT_DEFAULTS } from '../PanelLayout.constants';
import {
  DefaultHeaderTitle,
  DefaultHeaderWrapper,
  FixedHeader,
  FixedHeaderInner,
  HeaderPrimary,
  ScrollViewport,
  ShellRoot,
} from '../PanelLayout.styles';
import { PanelLayoutLoader, PanelLayoutProvider, usePanelLayout } from '../PanelLayoutContext';
import { ThreeColumnPanel } from '../Panels/ThreeColumnPanel';
import { PanelTabHeader } from '../PanelTabHeader';

import type { PanelTab } from '../PanelTabHeader';
import type { CSSProperties, ReactNode } from 'react';

type PersistedSidebarState = {
  leftCollapsed: boolean;
  rightCollapsed: boolean;
};

type DefaultPanelLayoutBaseProps = {
  appHeader?: ReactNode;
  contentPadding?: string;
  defaultLeftCollapsed?: boolean;
  defaultRightCollapsed?: boolean;
  header?: ReactNode;
  headerHeight?: string;
  hideHeader?: boolean;
  leftSidebarWidth?: string;
  main: ReactNode;
  resetSidebarOnPathChange?: boolean;
  rightSidebarWidth?: string;
  sidebarContentPadding?: boolean;
  tabs?: PanelTab[];
};

const DefaultPanelLayoutContent = ({
  appHeader,
  contentPadding = PANEL_LAYOUT_DEFAULTS.contentPadding,
  defaultLeftCollapsed = false,
  defaultRightCollapsed = false,
  header,
  headerHeight = PANEL_LAYOUT_DEFAULTS.headerHeight,
  hideHeader = false,
  leftSidebarWidth = PANEL_LAYOUT_DEFAULTS.leftSidebarWidth,
  main,
  resetSidebarOnPathChange = true,
  rightSidebarWidth = PANEL_LAYOUT_DEFAULTS.rightSidebarWidth,
  tabs,
}: DefaultPanelLayoutBaseProps) => {
  const pathname = usePathname();
  const {
    hasLeftSidebar,
    hasRightSidebar,
    headerContent,
    headerTabs,
    headerTitleText,
    isLeftCollapsed,
    isLeftSidebarPadded,
    isMainContentPadded,
    isRightCollapsed,
    isRightSidebarPadded,
    setSidebarCollapsedState,
    setSidebarPortalTargetLeft,
    setSidebarPortalTargetRight,
  } = usePanelLayout();
  const [hasLoadedPersistedSidebarState, setHasLoadedPersistedSidebarState] = useState(false);
  const [fallbackTitle, setFallbackTitle] = useState('');
  const isSmallViewport = useMediaQuery({ maxWidth: 768 });

  const leftSidebarRef = useCallback(
    (el: HTMLDivElement | null) => setSidebarPortalTargetLeft(el),
    [setSidebarPortalTargetLeft],
  );

  const rightSidebarRef = useCallback(
    (el: HTMLDivElement | null) => setSidebarPortalTargetRight(el),
    [setSidebarPortalTargetRight],
  );
  const resolvedTabs = tabs ?? headerTabs;
  const sidebarStateStorageKey = useMemo(() => `panel-layout:sidebar-state:${pathname}`, [pathname]);
  const hasHeaderControls = appHeader != null;

  useEffect(() => {
    if (isSmallViewport) {
      setSidebarCollapsedState({ leftCollapsed: true, rightCollapsed: true });
    }
  }, [isSmallViewport, setSidebarCollapsedState]);

  useEffect(() => {
    // On small viewports, always force sidebars collapsed — never restore persisted open state.
    if (isSmallViewport) {
      setSidebarCollapsedState({ leftCollapsed: true, rightCollapsed: true });
      setHasLoadedPersistedSidebarState(true);

      return;
    }

    const hasWindow = globalThis.window !== undefined;

    if (hasWindow) {
      const persistedStateRaw = globalThis.localStorage.getItem(sidebarStateStorageKey);

      if (persistedStateRaw) {
        try {
          const persistedState = JSON.parse(persistedStateRaw) as Partial<PersistedSidebarState>;

          setSidebarCollapsedState({
            leftCollapsed:
              typeof persistedState.leftCollapsed === 'boolean' ? persistedState.leftCollapsed : defaultLeftCollapsed,
            rightCollapsed:
              typeof persistedState.rightCollapsed === 'boolean'
                ? persistedState.rightCollapsed
                : defaultRightCollapsed,
          });

          setHasLoadedPersistedSidebarState(true);

          return;
        } catch {
          // Ignore malformed persisted state and fall back to default behaviour.
        }
      }
    }

    if (resetSidebarOnPathChange) {
      setSidebarCollapsedState({
        leftCollapsed: defaultLeftCollapsed,
        rightCollapsed: defaultRightCollapsed,
      });
    }

    setHasLoadedPersistedSidebarState(true);
  }, [
    defaultLeftCollapsed,
    defaultRightCollapsed,
    isSmallViewport,
    resetSidebarOnPathChange,
    setSidebarCollapsedState,
    sidebarStateStorageKey,
  ]);

  useEffect(() => {
    if (globalThis.window === undefined || !hasLoadedPersistedSidebarState || isSmallViewport) {
      return;
    }

    const stateToPersist: PersistedSidebarState = {
      leftCollapsed: isLeftCollapsed,
      rightCollapsed: isRightCollapsed,
    };

    globalThis.localStorage.setItem(sidebarStateStorageKey, JSON.stringify(stateToPersist));
  }, [hasLoadedPersistedSidebarState, isLeftCollapsed, isRightCollapsed, isSmallViewport, sidebarStateStorageKey]);

  useEffect(() => {
    if (globalThis.document === undefined) {
      return;
    }

    setFallbackTitle(globalThis.document.title);
  }, [pathname]);

  useEffect(() => {
    if (globalThis.document === undefined || headerTitleText == null) {
      return;
    }

    globalThis.document.title = headerTitleText;
  }, [headerTitleText]);

  const styleOverrides: CSSProperties = useMemo(() => {
    const overrides: Record<string, string> = {};

    if (headerHeight !== PANEL_LAYOUT_DEFAULTS.headerHeight) {
      overrides['--layout-header-height'] = headerHeight;
    }

    return overrides as CSSProperties;
  }, [headerHeight]);
  const resolvedTitleNode =
    headerContent ?? (fallbackTitle ? <DefaultHeaderTitle>{fallbackTitle}</DefaultHeaderTitle> : null);

  return (
    <ShellRoot style={styleOverrides}>
      {appHeader}
      <FixedHeader $hidden={hideHeader}>
        <FixedHeaderInner>
          {header ? (
            <HeaderPrimary>{header}</HeaderPrimary>
          ) : (
            <HeaderPrimary>
              {resolvedTitleNode && <DefaultHeaderWrapper>{resolvedTitleNode}</DefaultHeaderWrapper>}
              {resolvedTabs && <PanelTabHeader tabs={resolvedTabs} />}
            </HeaderPrimary>
          )}
        </FixedHeaderInner>
      </FixedHeader>
      <ScrollViewport>
        <ThreeColumnPanel
          contentPadding={contentPadding}
          defaultLeftCollapsed={defaultLeftCollapsed}
          defaultRightCollapsed={defaultRightCollapsed}
          isDrawerMode={isSmallViewport}
          leftCollapsed={isLeftCollapsed}
          leftSidebar={hasLeftSidebar ? <div ref={leftSidebarRef} /> : undefined}
          leftSidebarPadded={isLeftSidebarPadded}
          leftSidebarWidth={leftSidebarWidth}
          mainContentPadded={isMainContentPadded}
          onLeftCollapsedChange={(collapsed) =>
            setSidebarCollapsedState({
              leftCollapsed: collapsed,
              rightCollapsed: isRightCollapsed,
            })
          }
          onRightCollapsedChange={(collapsed) =>
            setSidebarCollapsedState({
              leftCollapsed: isLeftCollapsed,
              rightCollapsed: collapsed,
            })
          }
          rightCollapsed={isRightCollapsed}
          rightSidebar={hasRightSidebar ? <div ref={rightSidebarRef} /> : undefined}
          rightSidebarPadded={isRightSidebarPadded}
          rightSidebarWidth={rightSidebarWidth}
          showSidebarControls={!hasHeaderControls}
        >
          {main}
        </ThreeColumnPanel>
        <PanelLayoutLoader />
      </ScrollViewport>
    </ShellRoot>
  );
};

export const DefaultPanelLayout = ({ children, ...props }: DefaultPanelLayoutBaseProps & { children?: ReactNode }) => (
  <PanelLayoutProvider>
    {children}
    <DefaultPanelLayoutContent {...props} />
  </PanelLayoutProvider>
);
